import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, Car, Users, Wallet, Handshake, Briefcase, UserPlus, Store, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";

const NAV = [
  { to: "/admin/inventario", label: "Inventario", icon: Car, slug: "inventario" },
  { to: "/admin/consignantes", label: "Consignantes", icon: Users, slug: "consignantes" },
  { to: "/admin/compradores", label: "Compradores", icon: Wallet, slug: "compradores" },
  { to: "/admin/socios", label: "Socios comerciales", icon: Handshake, slug: "socios" },
  { to: "/admin/solicitudes-socios", label: "Solicitudes de socios", icon: Briefcase, slug: "solicitudes-socios" },
  { to: "/admin/solicitudes-afiliados", label: "Solicitudes de afiliados", icon: UserPlus, slug: "solicitudes-afiliados" },
  { to: "/admin/inventario-seminuevos", label: "Inventario: Seminuevos", icon: Store, slug: "inventario-seminuevos" },
  { to: "/admin/inventario-nuevos", label: "Inventario: Nuevos", icon: Store, slug: "inventario-nuevos" },
  { to: "/admin/inventario-promociones", label: "Promociones", icon: Store, slug: "inventario-promociones" },
  { to: "/admin/interesados-inventario", label: "Interesados en Inventario", icon: Send, slug: "interesados-inventario" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const doLogout = () => {
    logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F1F3F3] text-[#3E5641]" data-testid="admin-shell">
      {/* Desktop sidebar — the single testid'd Logo lives here */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-[#3E5641]/10 bg-white p-6 md:flex md:flex-col">
        <Logo testId="brand-logo" />
        <nav className="mt-10 flex flex-col gap-1">
          {NAV.map(({ to, label, icon: Icon, slug }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors ${
                  isActive ? "bg-[#3E5641] text-white" : "text-[#3E5641] hover:bg-[#F1F3F3]"
                }`
              }
              data-testid={`admin-nav-${slug}`}
            >
              <Icon size={16} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto border-t border-[#3E5641]/10 pt-4">
          <p className="text-xs text-[#6B7678]">Conectado</p>
          <p className="text-sm text-[#3E5641]" data-testid="admin-user-email">{user?.email}</p>
          <button
            onClick={doLogout}
            className="mt-3 flex items-center gap-2 text-sm text-[#6B7678] hover:text-[#3E5641]"
            data-testid="admin-logout"
          >
            <LogOut size={14} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile top bar — Logo without testid so we don't duplicate the identifier
          in the DOM at the same time as the sidebar's copy. */}
      <div className="md:hidden border-b border-[#3E5641]/10 bg-white">
        <div className="flex items-center justify-between px-4 py-3">
          <Logo testId={null} />
          <button
            onClick={doLogout}
            className="text-sm text-[#6B7678]"
            data-testid="admin-mobile-logout"
          >
            Salir
          </button>
        </div>
        <nav className="flex overflow-x-auto border-t border-[#3E5641]/10 px-2">
          {NAV.map(({ to, label, slug }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-2 text-sm ${
                  isActive ? "border-b-2 border-[#C4674F] text-[#3E5641]" : "text-[#6B7678]"
                }`
              }
              data-testid={`admin-mnav-${slug}`}
            >
              {label}
            </NavLink>
          ))}
        </nav>
      </div>

      <main className="md:pl-64">
        <div className="mx-auto max-w-[1400px] p-6 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
